/*
Exports { ready, init, evaluate }

Optionally, initialize with
   init({packages:['numpy']})
or
   init({lazy:true, packages:['numpy']})
(If not initialized, it will be initialized on first call to evaluate, with packages=[].)

Then,
    {error} | {result, plots} = await evaluate(source_text, stdoutCallback)
*/

import { readable } from 'svelte/store';

const pyWorker = new Worker('pyworker.js', {type: 'module'})
let initPackages = [];

let seq = 0;
const resultHandler = {}; // {seq: {onSuccess, onError, stdoutCallback, stdoutBuffer}, ...}

let setReady = (r) => { initReady=r; };
let initReady = false;
export const ready = readable(false, function start(set) {
    setReady = (r) => { set(r); };
    if (initReady) set(initReady);
    return function stop() {};
});

// If defer=true, then don't start pyodide yet, just remember that when it does start
// we'll have to load all those packages
export function load(packages, defer) {
    packages = [...initPackages, ...packages.filter(p => !initPackages.includes(p))];
    if (defer)
        initPackages = packages;
    else {
        initPackages = [];
        setReady(false);
        seq += 1;
        resultHandler[seq] = { onError: (err) => { console.log("Error initializing Python worker", err.message); } };
        // console.log("Sending py request", {seq, packages: (packages ?? []).join('\n')});
        pyWorker.postMessage({packages, seq});
    }
}

pyWorker.onerror = function(event) {
    const msgSeq = event.seq ?? seq;
    const { onError } = resultHandler[msgSeq] ?? {};
    onError(event);
    if (msgSeq == seq) setReady(true);
}

pyWorker.onmessage = function(event) {
    const { seq:msgSeq, stdout } = event.data;
    const { stdoutCallback, stdoutBuffer, onSuccess } = resultHandler[msgSeq] ?? {};
    if (stdout != null) {
        if (stdoutCallback != null) stdoutCallback(stdout);
        if (stdoutBuffer != null) stdoutBuffer.push(stdout);
    }
    else {
        const res = {...event.data};
        if (stdoutBuffer != null) res['stdout'] = stdoutBuffer.join('\n');
        if (onSuccess) onSuccess(res);
        else if (res.error && resultHandler[msgSeq]?.onError)
            resultHandler[msgSeq].onError(new Error(res.error));
        delete resultHandler[msgSeq];
        if (msgSeq == seq) setReady(true);
    }
}


export function evaluate(text, stdoutCallback) {
    setReady(false);
    return new Promise(function(onSuccess, onError) {
        seq += 1;
        resultHandler[seq] = {
            onSuccess,
            onError,
            stdoutCallback,
            stdoutBuffer: (stdoutCallback != null) ? null : []
        };
        const packages = (initPackages.length > 0) ? initPackages : null;
        initPackages = [];
        // console.log("Sending py request", {seq, packages: (packages ?? []).join('\n'), source: true});
        pyWorker.postMessage({source:text, seq, packages});
    });
}
